import { z } from "zod";
import { CLAVES } from "../sat/catalogos.js";
const RFC_REGEX = /^([A-ZÑ&]{3,4})\d{6}([A-Z\d]{3})$/;
/**
 * El RFC de persona moral tiene 12 caracteres (3 letras de razón social) y
 * el de persona física 13 (4 letras: apellidos y nombre).
 */
export const esPersonaMoral = (rfc) => rfc.trim().length === 12;
export const esPersonaFisica = (rfc) => rfc.trim().length === 13;
/**
 * Regímenes que el SAT reserva a un solo tipo de persona.
 *
 * Es un subconjunto conservador: solo los que no admiten ambigüedad. El 610
 * (residentes en el extranjero), el 622 y el 626 (RESICO) aplican a personas
 * físicas y morales, así que no se validan aquí.
 *
 * Sirve para atajar el error más común al dar de alta un cliente —capturar
 * "601 General de Ley Personas Morales" con el RFC de una persona física—,
 * que el SAT rechaza hasta el momento del timbrado.
 */
export const REGIMENES_SOLO_MORAL = ["601", "603", "620", "623", "624"];
export const REGIMENES_SOLO_FISICA = [
    "605",
    "606",
    "607",
    "608",
    "611",
    "612",
    "614",
    "615",
    "616",
    "621",
    "625",
];
export function validarRegimenContraRfc(rfc, regimenFiscal) {
    if (!RFC_REGEX.test(rfc))
        return null;
    const moral = esPersonaMoral(rfc);
    if (moral && REGIMENES_SOLO_FISICA.includes(regimenFiscal)) {
        return `El régimen ${regimenFiscal} es de persona física, pero el RFC tiene 12 caracteres (persona moral)`;
    }
    if (!moral && REGIMENES_SOLO_MORAL.includes(regimenFiscal)) {
        return `El régimen ${regimenFiscal} es de persona moral, pero el RFC tiene 13 caracteres (persona física)`;
    }
    return null;
}
/**
 * Campos base. Se define aparte del esquema con reglas cruzadas porque
 * `.superRefine()` devuelve un ZodEffects, y sobre eso ya no se puede
 * llamar `.partial()` para construir el esquema de actualización.
 */
export const clienteCamposSchema = z.object({
    rfc: z.string().trim().toUpperCase().regex(RFC_REGEX, "RFC inválido"),
    /**
     * Sin régimen societario ("SA DE CV") y tal como aparece en la Constancia
     * de Situación Fiscal. En CFDI 4.0 el SAT valida nombre + RFC + CP contra
     * su padrón, y cualquier diferencia rechaza el timbrado.
     */
    razonSocial: z.string().trim().min(1).max(300),
    nombreComercial: z.string().trim().max(300).optional(),
    regimenFiscal: z.enum(CLAVES.regimenFiscal),
    codigoPostal: z.string().regex(/^\d{5}$/, "código postal inválido"),
    usoCfdiDefault: z.enum(CLAVES.usoCfdi).optional(),
    formaPagoDefault: z.enum(CLAVES.formaPago).optional(),
    email: z.email("correo inválido").optional().or(z.literal("")),
    telefono: z.string().trim().max(40).optional(),
    sitioWeb: z.string().trim().max(200).optional(),
    diasCredito: z.number().int().min(0).max(365).default(0),
    notas: z.string().max(4000).optional(),
});
const coherenciaRfcRegimen = (datos, ctx) => {
    if (!datos.rfc || !datos.regimenFiscal)
        return;
    const problema = validarRegimenContraRfc(datos.rfc, datos.regimenFiscal);
    if (problema) {
        ctx.addIssue({ code: "custom", path: ["regimenFiscal"], message: problema });
    }
};
export const clienteCreateSchema = clienteCamposSchema.superRefine(coherenciaRfcRegimen);
export const clienteUpdateSchema = clienteCamposSchema
    .partial()
    .extend({ activo: z.boolean().optional() })
    .superRefine(coherenciaRfcRegimen);
// --- Contactos --------------------------------------------------------------
export const contactoCamposSchema = z.object({
    nombre: z.string().trim().min(1).max(200),
    puesto: z.string().trim().max(120).optional(),
    email: z.email("correo inválido").optional().or(z.literal("")),
    telefono: z.string().trim().max(40).optional(),
    esPrincipal: z.boolean().default(false),
});
export const contactoCreateSchema = contactoCamposSchema;
export const contactoUpdateSchema = contactoCamposSchema.partial();
// --- Consultas --------------------------------------------------------------
export const listaClientesQuerySchema = z.object({
    q: z.string().trim().optional(),
    activo: z.stringbool().optional(),
    limite: z.coerce.number().int().min(1).max(200).default(50),
    cursor: z.string().optional(),
});
//# sourceMappingURL=cliente.js.map